import { Link } from 'react-router-dom';
import {
  Clock,
  DollarSign,
  AlertTriangle,
  Leaf,
  ChefHat,
  Scale,
  Layers,
  ExternalLink,
  ArrowRight,
  UtensilsCrossed,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { Recipe } from '../../types';
import {
  RECIPE_TYPE_LABELS, DIFFICULTY_LABELS, DIFFICULTY_COLORS 
} from '../../types';

const typeColors = {
  finished: 'bg-blue-100 text-blue-800',
  'sub-recipe': 'bg-purple-100 text-purple-800',
  component: 'bg-gray-100 text-gray-800',
};

const ALLERGEN_LABELS: Record<string, string> = {
  milk: 'Milk',
  eggs: 'Eggs',
  fish: 'Fish',
  shellfish: 'Shellfish',
  'tree-nuts': 'Tree Nuts',
  peanuts: 'Peanuts',
  wheat: 'Wheat',
  soybeans: 'Soybeans',
  sesame: 'Sesame',
};

const DIETARY_FLAG_LABELS: Record<string, string> = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  'gluten-free': 'GF',
  'dairy-free': 'DF',
  halal: 'Halal',
  kosher: 'Kosher',
};

interface DetailsTabProps {
  readonly recipe: Recipe;
  readonly subRecipes: Recipe[];
  readonly onNavigate: (recipe: Recipe) => void;
}

export function DetailsTab({
  recipe, subRecipes, onNavigate 
}: DetailsTabProps) {
  const totalTime = (recipe.prepTime ?? 0) + (recipe.cookTime ?? 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <Clock className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
          <p className="text-xs text-muted-foreground">Prep Time</p>
          <p className="font-semibold">{recipe.prepTime ?? 0} min</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <Clock className="h-5 w-5 mx-auto text-orange-500 mb-1" />
          <p className="text-xs text-muted-foreground">Cook Time</p>
          <p className="font-semibold">{recipe.cookTime ?? 0} min</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <Scale className="h-5 w-5 mx-auto text-blue-500 mb-1" />
          <p className="text-xs text-muted-foreground">Yield</p>
          <p className="font-semibold">
            {recipe.yield ?? recipe.servings} {recipe.yieldUnit ?? 'servings'}
          </p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <DollarSign className="h-5 w-5 mx-auto text-green-500 mb-1" />
          <p className="text-xs text-muted-foreground">Cost/Portion</p>
          <p className="font-semibold">
            ${(recipe.costPerPortion ?? recipe.costPerServing ?? 0).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {recipe.difficulty && (
          <span className={cn('flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium', DIFFICULTY_COLORS[recipe.difficulty])}>
            <ChefHat className="h-4 w-4" />
            {DIFFICULTY_LABELS[recipe.difficulty]}
          </span>
        )}
        {totalTime > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
            <Clock className="h-4 w-4" />
            Total: {totalTime} min
          </span>
        )}
        <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
          {recipe.category}
        </span>
      </div>

      {recipe.description && (
        <div>
          <h3 className="font-medium mb-2">Description</h3>
          <p className="text-muted-foreground">{recipe.description}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        {recipe.allergens.length > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-muted-foreground">Allergens:</span>
            <div className="flex flex-wrap gap-1">
              {recipe.allergens.map((allergen) => (
                <span key={allergen} className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                  {ALLERGEN_LABELS[allergen] ?? allergen}
                </span>
              ))}
            </div>
          </div>
        )}
        {recipe.dietaryFlags.length > 0 && (
          <div className="flex items-center gap-2">
            <Leaf className="h-4 w-4 text-green-500" />
            <span className="text-sm text-muted-foreground">Dietary:</span>
            <div className="flex flex-wrap gap-1">
              {recipe.dietaryFlags.map((flag) => (
                <span key={flag} className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                  {DIETARY_FLAG_LABELS[flag] ?? flag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {subRecipes.length > 0 && (
        <div>
          <h3 className="font-medium mb-3 flex items-center gap-2">
            <Layers className="h-4 w-4 text-purple-500" />
            Sub-Recipes & Components ({subRecipes.length})
          </h3>
          <div className="grid gap-2">
            {subRecipes.map((sub) => (
              <button
                key={sub.recipeId}
                type="button"
                onClick={() => onNavigate(sub)}
                className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 transition-colors text-left group"
              >
                <div>
                  <p className="font-medium group-hover:text-primary transition-colors">{sub.name}</p>
                  <p className="text-sm text-muted-foreground">{sub.category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn('rounded-full px-2 py-1 text-xs font-medium', typeColors[sub.type])}>
                    {RECIPE_TYPE_LABELS[sub.type]}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t">
        <span>Version {recipe.version}</span>
        <span>Last updated: {new Date(recipe.updatedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

interface IngredientsTabProps {readonly recipe: Recipe;}

export function IngredientsTab({ recipe }: IngredientsTabProps) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left py-2 px-3 font-medium">Item</th>
            <th className="text-right py-2 px-3 font-medium">Quantity</th>
            <th className="text-left py-2 px-3 font-medium">Unit</th>
            <th className="text-left py-2 px-3 font-medium">Prep Notes</th>
          </tr>
        </thead>
        <tbody>
          {recipe.ingredients.map((ingredient) => (
            <tr key={ingredient.ingredientId} className={cn('border-t', ingredient.isOptional && 'opacity-70')}>
              <td className="py-2 px-3">
                <Link
                  to={`/catalog/items?search=${encodeURIComponent(ingredient.itemName)}`}
                  className="text-primary hover:underline inline-flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {ingredient.itemName}
                  <ExternalLink className="h-3 w-3" />
                </Link>
                {ingredient.isOptional && <span className="ml-2 text-xs text-muted-foreground">(optional)</span>}
              </td>
              <td className="py-2 px-3 text-right font-mono">{ingredient.quantity}</td>
              <td className="py-2 px-3 text-muted-foreground">{ingredient.unit}</td>
              <td className="py-2 px-3 text-muted-foreground">{ingredient.prepNotes ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface InstructionsTabProps {readonly instructions: string[];}

export function InstructionsTab({ instructions }: InstructionsTabProps) {
  return (
    <ol className="space-y-4">
      {instructions.map((step, stepIdx) => (
        <li key={`instruction-${stepIdx.toString()}`} className="flex gap-4">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
            {stepIdx + 1}
          </span>
          <p className="text-muted-foreground pt-1 flex-1">{step}</p>
        </li>
      ))}
    </ol>
  );
}

interface UsedInTabProps {
  readonly recipe: Recipe;
  readonly usedInRecipes: Recipe[];
  readonly onNavigate: (recipe: Recipe) => void;
}

export function UsedInTab({
  recipe, usedInRecipes, onNavigate 
}: UsedInTabProps) {
  if (usedInRecipes.length === 0) {
    return (
      <div className="text-center py-12">
        <UtensilsCrossed className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">
          This {recipe.type === 'component' ? 'component' : 'sub-recipe'} is not used in any recipes yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground mb-4">
        This {recipe.type === 'component' ? 'component' : 'sub-recipe'} is used in the following recipes:
      </p>
      {usedInRecipes.map((parentRecipe) => (
        <button
          key={parentRecipe.recipeId}
          type="button"
          onClick={() => onNavigate(parentRecipe)}
          className="w-full flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors text-left group"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-primary/10 p-2">
              <UtensilsCrossed className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium group-hover:text-primary transition-colors">{parentRecipe.name}</p>
              <p className="text-sm text-muted-foreground">
                {parentRecipe.category} · {parentRecipe.ingredients.length} ingredients
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('rounded-full px-2 py-1 text-xs font-medium', typeColors[parentRecipe.type])}>
              {RECIPE_TYPE_LABELS[parentRecipe.type]}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </button>
      ))}
    </div>
  );
}

export { typeColors };
